import { errorCopy, mediaAnalysisApi, photoDataUrl } from "../models/mediaAnalysisApi";

export const MEDIA_ANALYSIS_PERMISSION = "sales_pipeline.use_ai_media_analysis";
// One store per authenticated actor, outside individual message lifetimes.
export function createMediaAnalysisStore(api = mediaAnalysisApi, photo = photoDataUrl) {
  let generation = 0;
  let state = {};
  const checked = new Set();
  const listeners = new Set();
  const revisions = new Map();
  const update = (id, value) => {
    state = { ...state, [id]: { ...state[id], ...value } };
    listeners.forEach(listener => listener());
  };
  const store = {
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    getSnapshot: () => state,
    clear() {
      generation++; checked.clear(); revisions.clear(); state = {};
      listeners.forEach(listener => listener());
    },
    async check(id) {
      if (checked.has(id)) return;
      checked.add(id);
      const epoch = generation, revision = revisions.get(id);
      try {
        const result = await api.saved(id);
        if (epoch === generation && revision === revisions.get(id) && result && !state[id]?.result) {
          update(id, { result, ...(result.status === "failed" ? { error: result.errorMessage || "The analysis did not finish." } : {}) });
        }
      } catch { if (epoch === generation) checked.delete(id); }
    },
    toggle(id, params) {
      const current = state[id] || {};
      if (current.running) return;
      if (current.open || current.error || current.result?.status === "completed") update(id, { open: !current.open });
      else return store.analyse(id, params);
    },
    async analyse(id, params, again = false) {
      if (state[id]?.running) return;
      const epoch = generation;
      revisions.set(id, (revisions.get(id) || 0) + 1);
      update(id, { running: true, open: true, error: null });
      try {
        const mediaUrl = await photo(params.attachment);
        // Signing out during the byte download must not issue a paid AI call.
        if (epoch !== generation) return;
        const result = await api.analyse({ messageId: id, mediaUrl, conversationId: params.conversationId, customerPhone: params.customerPhone, again });
        if (epoch === generation) update(id, { result, error: result.status === "completed" ? null : result.errorMessage || "The analysis did not finish." });
      } catch (error) { if (epoch === generation) update(id, { error: errorCopy(error) }); }
      finally { if (epoch === generation) update(id, { running: false }); }
    },
  };
  return store;
}
